import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import QRCode from 'qrcode';
import { functions } from '../../firebase-client';
import './eventTickets.css';

const loadTickets = httpsCallable(functions, 'getMyEventAdmissionTickets');
const resendEmail = httpsCallable(functions, 'resendEventAdmissionEmail');
const loadCourseTickets = httpsCallable(functions, 'getMyCourseAdmissionTickets');

function coverageLabel(admission) {
  return admission.coverageType === 'course'
    ? 'Valid for every course session'
    : admission.coverageType === 'series'
    ? 'Valid for each remaining series occurrence'
    : admission.coverageType === 'occurrence'
      ? `Valid for ${admission.occurrenceId}`
      : 'Valid for this event';
}

function escapeHtml(value) {
  return String(value ?? '').replace(/[&<>'"]/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  }[character]));
}

export function buildTicketPrintHtml(data, qrUrls = {}) {
  const eventName = data?.event?.name || 'Event tickets';
  const passes = (data?.admissions || []).map(admission => {
    const active = admission.status === 'active';
    const qr = active && qrUrls[admission.admissionRef]
      ? `<img class="qr" src="${qrUrls[admission.admissionRef]}" alt="">`
      : `<div class="qr-placeholder">${active ? 'QR code unavailable — use the manual code below.' : 'This admission pass is no longer valid.'}</div>`;
    return `<article class="pass${active ? '' : ' void'}">
      <header><span>Member Nest ticket</span><strong>${active ? 'Valid' : 'Void'}</strong></header>
      <h2>${escapeHtml(eventName)}</h2>
      <p class="holder">${escapeHtml(admission.attendeeLabel)}</p>
      <p class="coverage">${escapeHtml(coverageLabel(admission))}</p>
      ${qr}
      <div class="code"><span>Manual code</span><strong>${escapeHtml(admission.shortCode)}</strong></div>
      <small>Ticket ${Number(admission.ordinal || 1)}. Staff may ask for the attendee or booking name.</small>
    </article>`;
  }).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${escapeHtml(eventName)} tickets</title><style>
    *{box-sizing:border-box}body{margin:0;background:#f1f5f9;color:#0f172a;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.toolbar{position:sticky;z-index:2;top:0;display:flex;justify-content:space-between;align-items:center;gap:1rem;padding:.8rem 1rem;background:#fff;border-bottom:1px solid #cbd5e1}.toolbar strong{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.toolbar button{min-height:44px;border:0;border-radius:10px;background:#2563eb;color:#fff;padding:.65rem 1rem;font:inherit;font-weight:750}.passes{display:grid;gap:1rem;width:min(100% - 1rem,640px);margin:1rem auto}.pass{break-inside:avoid;page-break-inside:avoid;background:#fff;border:1px solid #cbd5e1;border-radius:18px;padding:1.25rem;box-shadow:0 8px 24px rgba(15,23,42,.08)}.pass.void{opacity:.65}.pass header{display:flex;justify-content:space-between;color:#475569;font-size:.78rem;letter-spacing:.06em;text-transform:uppercase}.pass h2{margin:1rem 0 .35rem}.holder{font-size:1.15rem;font-weight:750}.coverage{color:#475569}.qr{display:block;width:min(100%,340px);margin:1rem auto}.qr-placeholder{display:grid;min-height:220px;place-items:center;margin:1rem 0;background:#f8fafc;padding:1rem;text-align:center}.code{margin:1rem 0;border-block:1px dashed #94a3b8;padding:.8rem;text-align:center}.code span{display:block;color:#64748b;font-size:.75rem}.code strong{font-size:1.5rem;letter-spacing:.14em}@media print{@page{margin:12mm}body{background:#fff}.toolbar{display:none}.passes{display:block;width:100%;margin:0}.pass{max-width:150mm;margin:0 auto;box-shadow:none;page-break-after:always}.pass:last-child{page-break-after:auto}}
  </style></head><body><div class="toolbar"><strong>${escapeHtml(eventName)}</strong><button type="button" onclick="window.print()">Print tickets</button></div><main class="passes">${passes || '<p>No admission passes were found.</p>'}</main></body></html>`;
}

function AdmissionPass({ admission, eventName }) {
  const [qrUrl, setQrUrl] = useState('');
  useEffect(() => {
    if (admission.status !== 'active') {
      setQrUrl('');
      return undefined;
    }
    let active = true;
    QRCode.toDataURL(admission.credential, { width: 360, margin: 2, errorCorrectionLevel: 'M' })
      .then(url => active && setQrUrl(url))
      .catch(() => active && setQrUrl(''));
    return () => { active = false; };
  }, [admission.credential, admission.status]);
  const coverage = coverageLabel(admission);
  return <article className={`admission-pass ${admission.status !== 'active' ? 'is-void' : ''}`}>
    <div className="admission-pass__header"><span>Member Nest ticket</span><strong>{admission.status === 'active' ? 'Valid' : 'Void'}</strong></div>
    <h2>{eventName}</h2>
    <p className="admission-pass__holder">{admission.attendeeLabel}</p>
    <p className="text-muted">{coverage}</p>
    {admission.status !== 'active'
      ? <div className="admission-pass__qr-placeholder">This admission pass is no longer valid.</div>
      : qrUrl
        ? <img className="admission-pass__qr" src={qrUrl} alt={`QR ticket for ${admission.attendeeLabel}`} />
        : <div className="admission-pass__qr-placeholder">Preparing QR…</div>}
    <div className="admission-pass__code"><span>Manual code</span><strong>{admission.shortCode}</strong></div>
    <small>Ticket {admission.ordinal}. Staff may ask for the attendee or booking name.</small>
  </article>;
}

export default function TicketWallet({ sourceType = 'event' }) {
  const { teamId, eventId, courseId } = useParams();
  const sourceId = sourceType === 'course' ? courseId : eventId;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    let active = true;
    setError('');
    const loader = sourceType === 'course' ? loadCourseTickets : loadTickets;
    const payload = sourceType === 'course' ? { teamId, courseId: sourceId } : { teamId, eventId: sourceId };
    loader(payload).then(result => active && setData(result.data)).catch(err => active && setError(err?.message || 'Could not load your tickets.'));
    return () => { active = false; };
  }, [sourceId, sourceType, teamId]);
  const resend = async () => {
    setBusy(true); setError(''); setNotice('');
    try {
      await resendEmail({ teamId, eventId: sourceId });
      setNotice('Ticket email sent.');
    }
    catch (err) { setError(err?.message || 'Could not resend the email.'); }
    finally { setBusy(false); }
  };
  const printTickets = async () => {
    setPrinting(true); setError(''); setNotice('');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setPrinting(false);
      setError('Your browser blocked the printable view. Allow pop-ups for Member Nest and try again.');
      return;
    }
    printWindow.document.open();
    printWindow.document.write('<!doctype html><title>Preparing tickets…</title><p style="font-family:sans-serif;padding:24px">Preparing printable tickets…</p>');
    printWindow.document.close();
    try {
      const qrEntries = await Promise.all((data.admissions || []).filter(admission => admission.status === 'active').map(async admission => [
        admission.admissionRef,
        await QRCode.toDataURL(admission.credential, { width: 600, margin: 2, errorCorrectionLevel: 'M' }),
      ]));
      printWindow.document.open();
      printWindow.document.write(buildTicketPrintHtml(data, Object.fromEntries(qrEntries)));
      printWindow.document.close();
      printWindow.opener = null;
      window.setTimeout(() => {
        try { printWindow.focus(); printWindow.print(); } catch { /* The visible print button remains available. */ }
      }, 350);
    } catch {
      try { printWindow.close(); } catch { /* Ignore a closed mobile print view. */ }
      setError('Could not prepare the printable tickets. Please try again.');
    } finally {
      setPrinting(false);
    }
  };
  if (error && !data) return <main className="ticket-wallet"><h1>{sourceType === 'course' ? 'Course tickets' : 'Event tickets'}</h1><p className="error-message">{error}</p><Link to="/teams">Back to clubs</Link></main>;
  if (!data) return <main className="ticket-wallet"><p>Loading tickets…</p></main>;
  return <main className="ticket-wallet">
    <header className="ticket-wallet__header">
      <div><span>Your admission passes</span><h1>{data.event?.name || 'Event tickets'}</h1><p>Show one QR code for each person arriving.</p></div>
      <div className="ticket-wallet__actions">{sourceType === 'event' && (data.admissions || []).some(pass => pass.status === 'active') && <button type="button" className="btn btn--secondary" onClick={resend} disabled={busy}>{busy ? 'Sending…' : 'Email me this link'}</button>}<button type="button" className="btn btn--secondary" onClick={printTickets} disabled={printing}>{printing ? 'Preparing…' : 'Print / save'}</button></div>
    </header>
    {notice && <p className="status-message" role="status">{notice}</p>}{error && <p className="error-message" role="alert">{error}</p>}
    <section className="ticket-wallet__passes">{(data.admissions || []).map(pass => <AdmissionPass key={pass.admissionRef} admission={pass} eventName={data.event?.name || 'Event'} />)}</section>
    {(data.admissions || []).length === 0 && <p>No active admission passes were found.</p>}
  </main>;
}
